import {html, nothing, PropertyValues, ReactiveController} from "lit";
import {get, translate} from "lit-translate";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {when} from "lit/directives/when.js";
import {filter, takeUntil} from "rxjs";
import {container} from "tsyringe";
import {BulkCommand} from "../../../commands/bulk.command";
import {DeleteIssueCommand} from "../../../commands/delete-issue.command";
import {ICommand} from "../../../commands/i-command.interface";
import {EmployeeToIssueAssignment} from "../../../domain/entities/employee-to-issue-assignment.entity";
import {Issue} from "../../../domain/entities/issue.entity";
import {Project} from "../../../domain/entities/project.entity";
import {ArrayHelper} from "../../../helpers/array.helper";
import {UiHelper} from "../../../helpers/ui.helper";
import {BaseMessage} from "../../../message-broker/base-message.interface";
import {EntityPersistedPayload} from "../../../message-broker/payloads/entity-persisted.payload";
import {AbstractEntitiesDialogComponent} from "../../ha-dialog/abstract-entities-dialog.component";
import {DialogCommandName} from "../../ha-dialog/dialog-command.enum";
import {TabItem} from "../../ha-tab-list/tab-item.interface";
import {FormController} from "../../_reactive-controllers/form.controller";
import {CollapseToggleController} from "../../_reactive-controllers/collapse-toggle.controller";
import {ISSUE_STATUS_OPEN, NOTIFICATION_DURATION} from "../../../constants";
import {IssueType} from "../../../domain/entities/issue-type.entity";
import {IssueStatus} from "../../../domain/entities/issue-status.entity";
import {IssueStatusRepository} from "../../../domain/repositories/issue-status.repository";
import {MessageBroker} from "../../../message-broker/message-broker";
import {IssueCommandBuilderService, IssueDialogRendererService, IssueFormService} from "../../../services";
import {AuthorizationService} from "../../../services/authorization.service";
import {CommandName} from "../../../commands/command-name.enum";
import {ExecutionVerification} from "../../../services/execution-verification.type";
import {resolveIssueDialogTitleKey} from "./issue-dialog-title.helper";
import {resolveVacationTitleForAssignmentChange} from "./vacation-auto-title.helper";
import {isTypeChoicePending, typePickerState} from "./issue-type-picker.helper";
import {loadedIssueTypes} from "../../../helpers/loaded-issue-types.helper";
import {isAbsenceType, needsOpenStatus, statusAfterTypeChange} from "./absence-status.helper";
import {PrimitiveFormControl} from "@ha/formhandler";
import {Employee} from "../../../domain/entities/employee.entity";
import type {HaRteComponent} from "../../ha-rte/ha-rte.component";
import {rootUidOf} from "../../../domain/helpers/issue-type-root.helper";

/**
 * Form data type - must match the service definitions
 */
type FormData = {
    title: string,
    status: any,
    type: IssueType,
    project: any,
    keywords: any[],
    timeRange: any,
    creator: any,
    description: any,
    employeeToIssueAssignments: any[]
}

/**
 * **ORCHESTRATOR PATTERN IN ACTION**
 *
 * This is the new, simplified Dialog component that acts as an Orchestrator.
 * It coordinates services instead of implementing functionality itself.
 *
 * **From 867 lines to ~300 lines (65% reduction)**
 *
 * **SOLID Principles Applied:**
 * - Single Responsibility: Only coordinates services
 * - Dependency Inversion: Depends on service interfaces
 * - Open/Closed: Can be extended by adding new services
 *
 * **What the Dialog DOES (Orchestration):**
 * - Coordinates service calls
 * - Handles event flow
 * - Manages component lifecycle
 * - Dispatches commands to parent
 *
 * **What the Dialog DOESN'T DO (Delegated to Services):**
 * - Form management → IssueFormService
 * - Command building → IssueCommandBuilderService
 * - UI rendering → IssueDialogRendererService
 * - Business logic → Services handle this
 */
@customElement('ha-dialog-issue-edit')
export class HaDialogIssueEditComponent extends AbstractEntitiesDialogComponent<Issue> {

    @property({type: String})
    public title = get('ha-boards-view.edit-issue.header.default');

    /**
     * **DEPENDENCY INJECTION - SERVICES RESOLVED**
     *
     * Since @inject decorators don't work reliably in Lit components,
     * we resolve services via container.resolve() in connectedCallback.
     * This ensures services are available when the component initializes.
     */
    private formService!: IssueFormService;
    private commandBuilder!: IssueCommandBuilderService;
    private renderer!: IssueDialogRendererService;

    // **SIMPLIFIED STATE - Much less than before**
    private _selectedTabItemIndex: number = 0;

    @state()
    private _tabItems: TabItem<Issue>[] = [];

    @state()
    private _totalChangedIssues = 0;

    // **FORM CONTROLLER - Now managed by service**
    private _formController!: FormController<FormData>;

    private _isSelectedEntityReadOnly = true;

    /** Creator + audit log below the form, folded away behind the bar above
     *  them (Anke, 2026-09-25). Starts collapsed on every open — a new dialog
     *  element is created each time — and keeps its state across tab switches. */
    private readonly _hiddenInfos = new CollapseToggleController(this, true);

    /** Phone width only (max 768px, Anke, 2026-10-18): a button at the bottom of the screen
     *  folds the sidebar (buttons + issue tabs) away and back in. Starts collapsed on every
     *  open, like `_hiddenInfos`. Above 768px ralphy.css hides the button and ignores the
     *  state, so the sidebar always shows there. */
    private readonly _mobileSidebar = new CollapseToggleController(this, true);

    private _pendingCloseAfterSave = false;
    private _lastDeletableState: boolean | null = null;

    /**
     * **COMPUTED PROPERTIES - Simple delegations**
     */
    private get _isDeletable(): boolean {
        if (!this.selectedEntity) return false;

        // Regel 1: Neue Issues sind immer löschbar (Dialog schließen)
        if (this.selectedEntity.isNew()) return true;

        // Regel 2: Bereits gelöschte Issues nicht nochmals löschbar (Edge Case)
        if (this.selectedEntity.deletedAt !== null) return false;

        // Regel 3: Verwende Service-Cache (wurde durch Preload gefüllt)
        // Note: Dieser Getter wird synchron aufgerufen, aber die Permissions wurden
        // bereits async preloaded. Falls noch nicht geladen, conservative fallback.
        try {
            // Quick sync check - falls Service-Cache bereits verfügbar
            const authService = container.resolve(AuthorizationService);

            // Service cached bereits alle Permissions via Preload
            // Für neue async Authorization-Check falls nicht im Cache
            authService.canExecute([{
                entity: this.selectedEntity,
                commandName: CommandName.DELETE_ISSUE,
                canExecute: false
            }]).then(results => {
                const newIsDeletable = ArrayHelper.first(results)?.canExecute ?? false;
                // Trigger UI update falls State sich ändert
                if (newIsDeletable !== this._lastDeletableState) {
                    this._lastDeletableState = newIsDeletable;
                    this.requestUpdate();
                }
            });

            // Return current cached state or conservative default
            return this._lastDeletableState ?? false;

        } catch (error) {
            console.warn('[IssueDialog] Authorization check failed:', error);
            return false;
        }
    }

    private get _isSavable(): boolean {
        // FormController's own isSavable, plus: no save while the type is still an abstract placeholder.
        return (this._formController?.isSavable || false) && !this._isTypeChoicePendingIn(this._formController);
    }

    private get _isSomeEntitySavable(): boolean {
        return Array.from(this.formService.getAllFormControllers().values())
            .some(formCtl => formCtl.form?.isDirty && formCtl.form?.isValid && !this._isTypeChoicePendingIn(formCtl));
    }

    /**
     * Whether a form's issue still sits on an abstract type (the placeholder a new absence starts on when
     * its root offers several children). No save path may send it: the backend refuses an abstract type,
     * and a refused command rolls back the whole batch. Checked on the type field itself, not through all
     * field errors: the date range validator is false for ranges under a day, and such issues exist and
     * must keep saving their other changes.
     */
    /** The uid of the first dirty tab whose type is still an abstract placeholder, or null. */
    private _firstDirtyTabWithPendingType(): string | null {
        for (const [uid, controller] of this.formService.getAllFormControllers()) {
            if (controller.isDirty && this._isTypeChoicePendingIn(controller)) {
                return uid;
            }
        }
        return null;
    }

    private _isTypeChoicePendingIn(controller: typeof this._formController | undefined): boolean {
        return isTypeChoicePending(controller?.form?.getField<IssueType, unknown>('type')?.getValue() ?? null);
    }

    /**
     * Determines if the "Save and Close" button should be enabled.
     * Only enabled when exactly the current entity has unsaved changes.
     */
    private get _isSaveAndCloseEnabled(): boolean {
        // Check if the current form is dirty and its type is not an abstract placeholder
        if (!this._formController?.isDirty || this._isTypeChoicePendingIn(this._formController)) {
            return false;
        }

        // Count totally changed issues
        let changedCount = 0;
        for (const controller of this.formService.getAllFormControllers().values()) {
            if (controller.form?.isDirty) {
                changedCount++;
                if (changedCount > 1) {
                    return false; // More than one changed issue
                }
            }
        }

        return changedCount === 1;
    }

    /**
     * **LIFECYCLE METHODS - Event setup**
     */
    public connectedCallback() {
        super.connectedCallback();

        // **RESOLVE SERVICES - Critical initialization**
        try {
            this.formService = container.resolve(IssueFormService);
            this.commandBuilder = container.resolve(IssueCommandBuilderService);
            this.renderer = container.resolve(IssueDialogRendererService);

        } catch (error) {
            console.error('[HaDialogIssueEdit] Failed to resolve services:', error);
            throw error;
        }

        if (this.selectedEntity) {
            this._setFormControllerByIssue(this.selectedEntity);
        }

        // **EVENT LISTENERS - React to entity.persisted events for clean architecture**
        // Includes child entities of the Issue aggregate (e.g. EmployeeToIssueAssignment)
        // so that saving only a child also resets the parent form's dirty state.
        container.resolve(MessageBroker).registerListener(this, {
            messageType: 'entity.persisted',
            method: this._onSomeIssuePersisted,
            filterFn: message =>
                message.payload.prototype === Issue ||
                message.payload.prototype === EmployeeToIssueAssignment,
            disconnect: this._disconnected$.asObservable()
        });

        // **AUTHORIZATION PRELOAD - Batch load all permissions for performance**
        this._preloadAllAuthorizations().then();

    }

    public disconnectedCallback() {
        // **CLEANUP - Delegate to service**
        this.formService.clearAllFormControllers();
        super.disconnectedCallback();
    }

    protected willUpdate(changedProperties: PropertyValues): void {
        // Before the render rebinds the editor to the next issue's form: what it still holds back
        // belongs to the issue being left (Anke, 2026-10-10: text typed in a clone showed up in the
        // original). Not on the first assignment, there is nothing to hand over yet.
        if (changedProperties.has('selectedEntity') && changedProperties.get('selectedEntity')) {
            this._handOverEditors();
        }

        if (changedProperties.has('selectedEntity') && this.selectedEntity) {
            this._selectedTabItemIndex = this._tabItems.findIndex(tab => tab.payload === this.selectedEntity);
            this._setFormControllerByIssue(this.selectedEntity);
// Authorization state is now preloaded and cached by service
        }

        if (changedProperties.has('entities')) {
            this._rebuildTabItems();
            this._preloadAllAuthorizations(); // Reload authorization when entities change
        }
    }

    /**
     * **MAIN CONTENT RENDERING - Delegated to Service**
     *
     * Before: 4 separate render methods with 120+ lines
     * After: Service calls with parameter injection
     */
    /**
     * Type-aware dialog title (create vs. edit × the current issue's type),
     * reflecting whichever tab is currently active in bulk-edit — selectedEntity
     * IS the active tab's entity (set on tab click, see the tab-click handler
     * below), so no extra wiring is needed beyond reading it fresh on every
     * render. Falls back to null (→ this.title) before the first entity loads.
     *
     * Reacts to both required live-update cases through already-existing
     * reactivity, no new event wiring: a type change in the open form fires the
     * FormController's own requestUpdate() (see the form-event subscription
     * below), and a tab switch changes `selectedEntity`, which `updated()`
     * already handles by rebuilding the form controller for the newly selected
     * issue — both simply cause this method to run again on the next render.
     */
    protected _renderHeader(): unknown {
        if (!this.selectedEntity) {
            return null;
        }

        const issueType = this._formController.form?.getField<IssueType, unknown>('type')?.getValue() ?? null;

        // By root, so a child (e.g. an Absence child) gets its root's header.
        return get(resolveIssueDialogTitleKey(this.selectedEntity.isNew(), rootUidOf(issueType)));
    }

    protected _renderMainContent(): unknown {
        if (!this.selectedEntity) {
            return html`
                <div>Kein Task ausgewählt</div>`;
        }

        // **PARAMETER PREPARATION - Dialog's responsibility**
        const bindTo = this._formController.bindToFn;
        const issueType = this._formController.form?.getField<IssueType, unknown>('type')?.getValue() ?? null;
        const issueProject = this._formController.form?.getField<Project, unknown>('project')?.getValue() ?? null;
        const isTypeOfAbsence = isAbsenceType(issueType);

        // **STATE OBJECT - All UI state in one object**
        const state = {
            isSavable: this._isSavable,
            isDirty: this._formController?.isDirty || false,
            isDeletable: this._isDeletable,
            isTypeOfAbsence,
            isStatusless: issueType?.isStatusless ?? false,
            isHiddenInfosCollapsed: this._hiddenInfos.collapsed,
            hiddenInfosRegionId: this._hiddenInfos.regionId,
            isReadOnly: this._isSelectedEntityReadOnly,
            isSaveAndCloseEnabled: this._isSaveAndCloseEnabled,
            typePicker: typePickerState(issueType, loadedIssueTypes())
        };

        // **CALLBACKS OBJECT - All event handlers**
        const callbacks = {
            onSave: this._onClickSelectedEntitySaveButton.bind(this),
            onReset: this._onClickSelectedEntityResetButton.bind(this),
            onDelete: this._onClickSelectedEntityDeleteButton.bind(this),
            onClone: this._onClickSelectedEntityCloneButton.bind(this),
            onCopyLink: this._onClickCopyLinkToClipboard.bind(this),
            onSelectLink: this._onClickSelectLink.bind(this),
            onSaveAndClose: this._onClickSelectedEntitySaveAndCloseButton.bind(this),
            // The form's own notification is not enough to keep the button bar honest. The
            // library gates it with the field's compare function
            // (@ha/formhandler/dist/index.js:1079), so setting a time budget BACK to its
            // stored value notifies nobody: the form is clean again, and without a re-render
            // the save button stays visibly enabled. This re-render is unconditional and
            // therefore also covers the reverse case.
            //
            // It is bound HERE and not written into the template: that template lives in
            // IssueDialogRendererService, a service rather than a component, so `this` there
            // has no requestUpdate and the handler would fail silently on the first keystroke.
            // There is no tsc gate in the build to catch that.
            onUserInput: () => this.requestUpdate(),
            // A stable arrow field on the controller, so no binding is needed here.
            onToggleHiddenInfos: this._hiddenInfos.toggle
        };

        // **DATA OBJECT - External data needed**
        const data = {
            quickLink: this._buildIssueQuickLink(),
            issueType,
            selectedEntity: this.selectedEntity
        };

        // **SERVICE CALL - Complete rendering delegated**
        return html`
            <ha-form-issue data-type="${issueType?.uid || 'none'}" data-root-type=${rootUidOf(issueType) ?? nothing}
                          data-project="${issueProject?.uid || 'none'}">
                <main class="formular__main">
                    ${this.renderer.renderCompleteForm(bindTo, state, callbacks, data)}
                </main>
            </ha-form-issue>
        `;
    }

    /**
     * **SIDEBAR RENDERING - Simplified**
     */
    protected _renderSidebarLeft(): unknown {
        const regionId = this._mobileSidebar.regionId;
        return html`
            <button type="button" class="aside__toggle"
                    aria-expanded=${!this._mobileSidebar.collapsed}
                    aria-controls="${regionId}-header ${regionId}-main"
                    aria-label=${translate('components.ha-dialog.sidebar-toggle')}
                    title=${translate('components.ha-dialog.sidebar-toggle')}
                    @click=${this._mobileSidebar.toggle}></button>
            <header class="aside__header" id="${regionId}-header">
                <div class="button-group">
                    <button type="button" class="save-all buttonform-marker"
                            ?disabled=${!this._isSomeEntitySavable}
                            @click=${this._onClickSaveAllButton}
                            data-label=${translate('general.save-all')}><div class="hover-effect"></div>
                    </button>
                    <button type="button" class="save-and-close-all buttonform-marker"
                            ?disabled=${!this._isSomeEntitySavable}
                            @click=${this._onClickSaveAndCloseAllButton}
                            data-label=${translate('general.save-and-close-all')}><div class="hover-effect"></div>
                    </button>
                    <button type="button" class="reset-all buttonform-marker"
                            ?disabled=${!this._isSomeEntitySavable}
                            @click=${this._onClickResetAllButton}
                            data-label=${translate('general.reset-all')}><div class="hover-effect"></div>
                    </button>
                    ${when(!!this.template, () => html`
                        <button type="button" class="create buttonform-marker"
                                @click=${this._onClickCreateIssue}
                                data-label=${translate('general.create')}><div class="hover-effect"></div>
                        </button>
                    `)}
                </div>
            </header>
            <main class="aside__main" id="${regionId}-main">
                <ha-tab-list orientation="vertical"
                             .items=${this._tabItems}
                             .activeItemIndex=${this._selectedTabItemIndex}
                             @ha-tab.click=${this._onTabSelected}
                ></ha-tab-list>
            </main>
        `;
    }

    protected _onFormChanged(): void {
        let changedIssues = 0;
        for (const controller of this.formService.getAllFormControllers().values()) {
            if (controller.form?.isDirty) {
                changedIssues++;
            }
        }
        this._totalChangedIssues = changedIssues;
        this._rebuildTabItems();
    }

    /**
     * **AUTHORIZATION BATCH-PRELOAD - Performance optimization**
     */
    private async _preloadAllAuthorizations(): Promise<void> {
        if (!this.entities || this.entities.length === 0) return;

        // Filter entities that need authorization check (skip new/deleted)
        const entitiesToCheck = this.entities.filter(entity =>
            !entity.isNew() && entity.deletedAt === null
        );

        if (entitiesToCheck.length === 0) return;

        try {
            // Batch alle benötigten Commands für alle Entities
            const authRequests: ExecutionVerification[] = [];

            entitiesToCheck.forEach(entity => {
                // Commands die der Dialog benötigt
                [CommandName.DELETE_ISSUE,
                    CommandName.UPDATE_ISSUE,
                    CommandName.FINALIZE_ISSUE,
                    CommandName.CREATE_EMPLOYEE_TO_ISSUE_ASSIGNMENT,
                    CommandName.DELETE_EMPLOYEE_TO_ISSUE_ASSIGNMENT,
                    CommandName.UPDATE_EMPLOYEE_TO_ISSUE_ASSIGNMENT].forEach(commandName => {
                    authRequests.push({
                        entity,
                        commandName,
                        canExecute: false
                    });
                });
            });

            // Batch-Request - Service cached alle Results automatisch
            await container.resolve(AuthorizationService).canExecute(authRequests);

            // UI aktualisieren wenn Entity bereits selektiert
            if (this.selectedEntity) {
                this.requestUpdate();
            }

        } catch (error) {
            console.warn('[IssueDialog] Batch authorization preload failed:', error);
        }
    }

    /**
     * **FORM MANAGEMENT - Delegated to Service**
     *
     * Before: 75+ lines of form setup code
     * After: 3 lines of service delegation
     */
    private _setFormControllerByIssue(issue: Issue): void {

        // **SERVICE CALL - Form creation delegated**
        this._formController = this.formService.createFormController(this as ReactiveController, issue);
        this._isSelectedEntityReadOnly = this._checkIssueReadOnlyState(issue);

        // **EVENT SUBSCRIPTION - Still managed by dialog**
        this._formController.event$.pipe(
            takeUntil(this._disconnected$),
            filter(change => !change.event.startsWith('validation'))
        ).subscribe(() => this._rebuildTabItems());

        // **FORM STATE SUBSCRIPTION - Listen to all form changes for UI updates**
        this._formController.event$.pipe(
            takeUntil(this._disconnected$)
        ).subscribe(() => {
            // Force UI update when form state changes (especially isDirty)
            this.requestUpdate();
        });

        // **VACATION AUTO-TITLE** (Anke, 2026-08-19; D55 2026-10-20): for issues of
        // the Absence family the title is always auto-derived from the selected
        // employee ("<prefix> " + employee.name, the prefix being "Urlaub" on the
        // root and the child's own name on a child), re-derived every time the
        // assignment changes — and every time the type changes (below) — same
        // unconditional-for-the-type scoping as the max-1-assignment rule
        // above (`IssueDialogRendererService.renderEmployeeAssignments`), not
        // limited to creation-mode. Re-checks the CURRENT type on every
        // notification rather than the type at subscription time, since the
        // type combobox can be changed interactively while the dialog is open.
        // Removing the (sole) employee falls back to the bare "Urlaub" prefix
        // rather than leaving a stale "Urlaub <old name>" behind (review finding
        // 2026-08-20) — only for VACATION issues, non-vacation types are left
        // untouched either way.
        this._formController.event$.pipe(
            takeUntil(this._disconnected$),
            filter(notification => notification.formControl?.key === 'employeeToIssueAssignments')
        ).subscribe(async () => {
            const form = this._formController.form;
            if (!form) {
                return;
            }

            await this._applyVacationAutoTitle(form);
        });

        // **STATUS ON A TYPE CHANGE** (2026-08-27, reworked in step 7b): a statusless type (the Absence,
        // Holiday and Reminder families) gets no status, so switching TO one empties the status field
        // (D22: the backend drops a status sent for it anyway); an empty status on a type that carries
        // one becomes OPEN (_fillOpenStatusIfMissing); otherwise the status stays. The dialog no longer
        // forces REMINDER on any type (D19). The title sync above runs first.
        this._formController.event$.pipe(
            takeUntil(this._disconnected$),
            // Only the actual value change, not the 'validation.valid'/
            // 'validation.invalid' notification that fires right after it for
            // the same formControl.key (review finding 2026-08-27, Linse A).
            filter(notification => notification.formControl?.key === 'type' && notification.event === 'value.changed')
        ).subscribe(async () => {
            const form = this._formController.form;
            if (!form) {
                return;
            }

            await this._onTypeChanged(form);
        });
    }

    /**
     * What a type change in the dialog does: the absence title follows the new type (D55, "Krankheit
     * Müller"), and the status follows statusAfterTypeChange. One method, so a test of it covers both and
     * the e2e title check covers the subscription that calls it.
     */
    private async _onTypeChanged(form: NonNullable<typeof this._formController.form>): Promise<void> {
        await this._applyVacationAutoTitle(form);
        await this._applyStatusForType(form);
    }

    /**
     * Sets the status field for the form's current type (statusAfterTypeChange): empty for a statusless
     * type, OPEN for an empty status on a type that carries one, otherwise unchanged.
     */
    private async _applyStatusForType(form: NonNullable<typeof this._formController.form>): Promise<void> {
        const statusField = form.getField<IssueStatus | null, unknown>('status') as PrimitiveFormControl<IssueStatus | null, unknown>;
        const issueType = form.getField<IssueType, unknown>('type')?.getValue() ?? null;
        switch (statusAfterTypeChange(issueType, statusField.getValue())) {
            case 'clear':
                statusField.value = null;
                break;
            case 'fill-open':
                await this._fillOpenStatusIfMissing(form);
                break;
        }
    }

    /**
     * Sets the absence title for the form's current type and employee (vacation-auto-title.helper.ts);
     * leaves the title alone outside the Absence family. Reads type and employee when it runs, since the
     * user may change either while the dialog is open.
     */
    private async _applyVacationAutoTitle(form: NonNullable<typeof this._formController.form>): Promise<void> {
        const type = form.getField<IssueType, unknown>('type')?.getValue() ?? null;
        const assignments = form.getField<EmployeeToIssueAssignment[], unknown>('employeeToIssueAssignments')?.getValue() ?? [];
        const employee = await assignments[0]?.employee as Employee | undefined;

        const autoTitle = resolveVacationTitleForAssignmentChange(type, employee?.name);
        if (autoTitle === null) {
            return;
        }

        (form.getField<string, unknown>('title') as PrimitiveFormControl<string, unknown>).value = autoTitle;
    }

    /**
     * An issue without a status (a statusless issue, or its clone), switched to a type that carries one,
     * gets OPEN (Stefan, 2026-10-18), so no issue of such a type is saved without a status. Type and status
     * are read again after the lookup, as above, because the user may have switched the type again.
     */
    private async _fillOpenStatusIfMissing(form: NonNullable<typeof this._formController.form>): Promise<void> {
        const statusField = form.getField<IssueStatus, unknown>('status') as PrimitiveFormControl<IssueStatus, unknown>;
        if (!needsOpenStatus(form.getField<IssueType, unknown>('type')?.getValue() ?? null, statusField.getValue())) {
            return;
        }
        const openStatus = await container.resolve(IssueStatusRepository).findOneById(ISSUE_STATUS_OPEN);
        if (openStatus && needsOpenStatus(form.getField<IssueType, unknown>('type')?.getValue() ?? null, statusField.getValue())) {
            statusField.value = openStatus;
        }
    }

    /**
     * **SAVE OPERATIONS - Delegated to CommandBuilder Service**
     *
     * Before: 55+ lines of complex command building logic
     * After: Simple service calls
     */
    private async _onClickSelectedEntitySaveButton(): Promise<void> {
        this._flushPendingEditorChange();
        if (!this.selectedEntity || !this._formController) return;

        // **SERVICE CALL - Command building delegated**
        const commands = await this.commandBuilder.buildCommands(
            this.selectedEntity,
            this._formController
        );

        // **ORCHESTRATION - Dialog coordinates the flow**
        if (commands.length > 0) {
            this.dispatchCommand({
                command: DialogCommandName.OKAY,
                payload: new BulkCommand(commands)
            });
        }
    }

    private async _onClickSaveAllButton(): Promise<void> {
        this._flushPendingEditorChange();
        const commands: ICommand[] = [];

        // **BATCH PROCESSING - Using service for each issue**
        for (const [uid, controller] of this.formService.getAllFormControllers()) {
            // A tab whose type is still an abstract placeholder is left out, so it cannot roll back
            // the valid edits of the other tabs.
            if (controller.isDirty && !this._isTypeChoicePendingIn(controller)) {
                const issue = this._entities.find(e => e.uid === uid);
                if (issue) {
                    const issueCommands = await this.commandBuilder.buildCommands(issue, controller);
                    commands.push(...issueCommands);
                }
            }
        }

        if (commands.length > 0) {
            this.dispatchCommand({
                command: DialogCommandName.OKAY,
                payload: new BulkCommand(commands)
            });
        }
    }

    private async _onClickSaveAndCloseAllButton(): Promise<void> {
        // Picks up the editor's held-back change through _onClickSaveAllButton.
        await this._onClickSaveAllButton();
        // A dirty tab still on the abstract placeholder was not saved; closing would drop its edits
        // without a word, so the dialog stays open and shows that tab, whose type row asks for a child.
        const pendingUid = this._firstDirtyTabWithPendingType();
        if (pendingUid !== null) {
            this.selectedEntity = this._entities.find(entity => entity.uid === pendingUid) ?? this.selectedEntity;
            return;
        }
        this.dispatchCommand({
            command: DialogCommandName.CLOSE,
            payload: void 0
        });
    }

    /**
     * Handles "Save and Close" button click.
     * Saves the current entity and closes the dialog if successful.
     */
    private async _onClickSelectedEntitySaveAndCloseButton(): Promise<void> {
        this._flushPendingEditorChange();
        if (!this.selectedEntity || !this._formController) {
            console.warn('[HaDialogIssueEdit] Cannot save and close - no selected entity or form controller');
            return;
        }

        // Use existing save logic
        const commands = await this.commandBuilder.buildCommands(
            this.selectedEntity,
            this._formController
        );

        if (commands.length > 0) {
            // Dispatch save command
            this.dispatchCommand({
                command: DialogCommandName.OKAY,
                payload: new BulkCommand(commands)
            });

            // Close dialog after save
            // Note: The actual close happens after the entity.persisted event
            // to ensure the save was successful
            this._pendingCloseAfterSave = true;
        }
    }

    /**
     * **SIMPLE OPERATIONS - Remain in dialog**
     */
    private _onClickResetAllButton(): void {
        this._handOverEditors();
        for (const controller of this.formService.getAllFormControllers().values()) {
            controller.form?.reset();
        }
        this._rebuildTabItems();
    }

    private async _onClickCreateIssue(): Promise<void> {
        await this.createNewEntity();
    }

    private async _onClickSelectedEntityResetButton(): Promise<void> {
        this._handOverEditors();
        if (!this.selectedEntity) return;
        this._formController.form?.reset();
        this._rebuildTabItems();
    }

    /**
     * The rich-text editor waits 300 ms after the last keystroke before it reports a change. These
     * two make it report at once, into the form it is bound to right now.
     *
     * `_flushPendingEditorChange` before saving, so the last keystrokes are in the commands.
     * `_handOverEditors` before the editor gets another owner (switching issues) or the form is
     * reset: it also drops a picture whose upload is still running, which would otherwise land in
     * the next issue's text or in the text just reset. Reporting first matters for a reset too: the
     * form then differs from the loaded value, so `reset()` changes it and the editor reloads.
     */
    private _flushPendingEditorChange(): void {
        for (const rte of this._rteElements()) {
            rte.flushPendingChange();
        }
    }

    private _handOverEditors(): void {
        for (const rte of this._rteElements()) {
            rte.flushPendingChange();
            rte.abandonPendingInserts();
        }
    }

    private _rteElements(): HaRteComponent[] {
        return [...this.querySelectorAll<HaRteComponent>('ha-rte')];
    }

    private async _onClickSelectedEntityDeleteButton(): Promise<void> {
        if (!this.selectedEntity) return;

        const result = await UiHelper.confirm({
            title: get('ha-boards-view.confirm-delete.header'),
            content: html`<p>${translate('Der Task wird gelöscht.')}</p>`,
            confirmLabel: get('ha-boards-view.confirm-delete.submit'),
            cancelLabel: get('ha-boards-view.confirm-delete.cancel')
        });

        if (!result) return;

        if (!this.selectedEntity.isNew()) {
            this.dispatchCommand({
                command: DialogCommandName.OKAY,
                payload: new BulkCommand([new DeleteIssueCommand(this.selectedEntity)])
            });
        }

        const idx = this._entities.indexOf(this.selectedEntity);
        if (idx < 0) return;

        // When the last open entity is being deleted, close the dialog immediately.
        // The entities setter in AbstractEntitiesDialogComponent ignores empty arrays,
        // so we cannot rely on `this.entities.length < 1` after the splice below.
        if (this._entities.length <= 1) {
            this.dispatchCommand({
                command: DialogCommandName.CLOSE,
                payload: void 0
            });
            return;
        }

        this._withoutLastChangeValidation(() => this.entities = [...this.entities.slice(0, idx), ...this.entities.slice(idx + 1)]);

        this.selectedEntity = ArrayHelper.first(this.entities)!;
        this._rebuildTabItems();
    }

    private async _onClickSelectedEntityCloneButton(): Promise<void> {
        const selectedEntity = this._ensureSelectedEntity();
        if (!selectedEntity) {
            console.warn('[HaDialogIssueEdit] Cannot clone - no selected entity');
            return;
        }

        const clonedIssue = await selectedEntity.clone();
        this._entities = [clonedIssue, ...this._entities];
        this.selectedEntity = clonedIssue;
        this._rebuildTabItems();
    }


    /**
     * **UTILITY METHODS - Simple helpers**
     */
    private _buildIssueQuickLink(): string {
        const selectedEntity = this._ensureSelectedEntity();
        if (!selectedEntity?.uid) {
            return `${location.protocol}//${location.host}/issue/unknown`;
        }
        return `${location.protocol}//${location.host}/issue/${selectedEntity.uid}`;
    }

    private _onClickSelectLink(evt: Event): void {
        const range = document.createRange();
        range.selectNodeContents(evt.currentTarget as HTMLElement);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
    }

    private async _onClickCopyLinkToClipboard(): Promise<void> {
        const selectedEntity = this._ensureSelectedEntity();
        if (!selectedEntity?.uid) {
            UiHelper.notify({
                closable: true,
                title: 'Kopieren nicht möglich.',
                level: 'error',
                content: html`<p>Kein Task gefunden</p>`
            });
            return;
        }

        try {
            await navigator.clipboard.writeText(`${location.protocol}//${location.host}/issue/${selectedEntity.uid}`);
            UiHelper.notify({
                closable: true,
                title: 'URL kopiert',
                level: 'info',
                content: html`<p>Quicklink URL wurde in die Zwischenablage kopiert.</p>`,
                duration: NOTIFICATION_DURATION
            });
        } catch (err: any) {
            UiHelper.notify({
                closable: true,
                title: 'Kopieren nicht möglich.',
                level: 'error',
                content: html`<p>Konnte URL nicht in Zwischenablage einfügen:<br>${err.message ?? 'unbekannter Fehler'}
                </p>`
            });
        }
    }

    private _checkIssueReadOnlyState(issue: Issue): boolean {
        return (this._isReadOnly.has(issue) && this._isReadOnly.get(issue)) ?? false;
    }

    /**
     * **EVENT HANDLERS - Remain with dialog**
     */
    private _onTabSelected(evt: CustomEvent<{ value: TabItem<Issue> }>): void {
        this.selectedEntity = evt.detail.value.payload;
    }

    /**
     * **FORM STATE FIX FOR NEW ISSUES - MessageBroker-based form synchronization**
     *
     * This method handles entity.created events for new Issues and ensures form controllers
     * are properly synchronized with the entity state after successful creation.
     * Uses the same pattern as _onSomeIssueChanged for consistency.
     */
    /**
     * **FORM STATE FIX - Entity Persisted Event Handler**
     *
     * This method handles entity.persisted events for Issues and ensures form controllers
     * are properly synchronized with the persisted entity state.
     *
     * The entity.persisted event is published AFTER the entity has been marked as persisted
     * in the EntityManager, guaranteeing that issue.isNew() returns false.
     *
     * This unified handler works for both newly created and updated Issues.
     */
    private _onSomeIssuePersisted(messages: Array<BaseMessage<EntityPersistedPayload<Issue | EmployeeToIssueAssignment>>>): void {
        const dialogEntityUids = this._entities.map(e => e.uid);
        let hasRelevantChanges = false;

        // Process each persisted message safely
        for (const message of messages) {
            const {entity: persistedEntity} = message.payload;

            // Resolve the Issue UID: for an Issue itself, that's its uid;
            // for a child entity (e.g. EmployeeToIssueAssignment), follow the relation to its parent Issue.
            const issueUid: string | undefined = persistedEntity instanceof Issue
                ? persistedEntity.uid
                : persistedEntity.issue?.uid;

            if (!issueUid || !dialogEntityUids.includes(issueUid)) {
                continue;
            }

            const parentIssue = this._entities.find(e => e.uid === issueUid);
            if (!parentIssue) {
                continue;
            }

            hasRelevantChanges = true;

            // Keep what the user entered after clicking Save: this rebuilds the form from the issue as saved,
            // and without carrying the current values over, anything typed in the meantime is overwritten
            // silently. Until 2026-09-18 one save arrived here TWICE (the entity event was published by two
            // places), which widened that window to the ~500 ms between the two arrivals; the duplicate is
            // gone, but the window is not — the rebuild still races whatever the user is typing.
            const currentValues = this._currentFormValues(issueUid);

            // Clear old FormController for the parent Issue, recreate to reset dirty state
            this.formService.clearFormController(issueUid);

            // If this is the currently selected entity, set up new FormController properly
            if (this.selectedEntity?.uid === issueUid) {
                this._setFormControllerByIssue(parentIssue);
            } else {
                // For non-selected entities, just create the controller without event subscriptions
                this.formService.createFormController(this as ReactiveController, parentIssue);
            }

            this._restoreFormValues(issueUid, currentValues);
        }

        // Only rebuild tabs if we had relevant changes
        if (hasRelevantChanges) {
            this._rebuildTabItems();
        }

        // Handle close after save if pending
        if (hasRelevantChanges && this._pendingCloseAfterSave) {
            this._pendingCloseAfterSave = false;
            // Check if the saved entity was the only one with changes
            if (this._totalChangedIssues === 0) {
                // All saves completed, close dialog
                this.dispatchCommand({
                    command: DialogCommandName.CLOSE,
                    payload: void 0
                });
            }
        }
    }

    /**
     * Reads the current value of every field in the issue's form, keyed by field name. Empty when the issue has no
     * form yet.
     */
    private _currentFormValues(issueUid: string): Map<string, unknown> {
        const values = new Map<string, unknown>();
        const form = this.formService.getFormControllerByUid(issueUid)?.form;
        if (!form) {
            return values;
        }
        for (const field of form.children) {
            if (field instanceof PrimitiveFormControl) {
                values.set(field.key, field.value);
            }
        }
        return values;
    }

    /**
     * Writes values read before a rebuild into the issue's new form. The new form's initial values are the issue as
     * saved, so a value that equals the saved one leaves its field clean, and a value entered after the save click
     * keeps its field dirty and the save button enabled.
     */
    private _restoreFormValues(issueUid: string, values: Map<string, unknown>): void {
        const form = this.formService.getFormControllerByUid(issueUid)?.form;
        if (!form) {
            return;
        }
        for (const [key, value] of values) {
            if (!form.hasField(key)) {
                continue;
            }
            const field = form.getField<unknown, unknown>(key);
            if (field instanceof PrimitiveFormControl) {
                field.value = value;
            }
        }
    }

    private _rebuildTabItems(): void {

        const selectedEntity = this._ensureSelectedEntity();
        if (!selectedEntity) {
            console.warn('[HaDialogIssueEdit] No selected entity - cannot rebuild tabs');
            return;
        }

        Promise.all(this._entities.map(async issue => {
            const formCtl = this.formService.getFormController(issue) ||
                this.formService.createFormController(this as ReactiveController, issue);
            const title = this._getFieldSafely(formCtl, 'title') || issue.title || 'Untitled';

            return {
                id: issue.uid,
                title: title,
                payload: issue,
                cssClasses: {
                    "has-changed-data": formCtl.isDirty || issue.isNew(),
                    "readonly": (this._isReadOnly.has(issue) && this._isReadOnly.get(issue)) ?? false
                }
            } as TabItem<Issue>;
        })).then(tabItems => {
            this._tabItems = tabItems;
            this._selectedTabItemIndex = Math.max(0,
                this._tabItems.findIndex(item => item.id === selectedEntity.uid)
            );
        });
    }

    /**
     * **DEFENSIVE PROGRAMMING HELPERS**
     * These methods provide safe access to potentially null/undefined values
     */

    /**
     * Ensures selectedEntity is available, returns null if not
     */
    private _ensureSelectedEntity(): Issue | null {
        return this.selectedEntity || null;
    }

    /**
     * Safely retrieves a field value from a form controller
     * @param controller Form controller to query
     * @param fieldName Name of the field to retrieve
     * @returns Field value or empty string if not found
     */
    private _getFieldSafely(controller: FormController<FormData>, fieldName: string): any {
        try {
            const field = controller.form?.getField(fieldName);
            return field?.getValue() ?? '';
        } catch (error) {
            console.warn(`[HaDialogIssueEdit] Failed to get field '${fieldName}':`, error);
            return '';
        }
    }
}